/**
 * ===================================================================
 * FRONTEND SERVICE - GROWING AREAS
 * ===================================================================
 * API sluzba pro spravu pestebnich oblasti.
 */

import { apiClient } from './api';
import {
  GrowingArea,
  AreaSlot,
  OccupancyResult,
  CreateGrowingAreaDTO,
  UpdateGrowingAreaDTO,
  PlantOccupancy,
  SlotEventsResult,
  SlotEventQueryParams,
  SlotEvent,
} from '../types/growing-areas';
import { RecommendationResult } from '../types/recommendations';

export interface RecommendationQueryParams {
  targetDate?: string;
  categories?: string;
  maxDifficulty?: number;
  hydroponicOnly?: boolean;
  limit?: number;
}

export const growingAreaService = {
  // Ziskej vsechny oblasti pro zarizeni
  getDeviceAreas: async (deviceUuid: string): Promise<GrowingArea[]> => {
    return apiClient.get<GrowingArea[]>(`/growing-areas/device/${deviceUuid}`);
  },

  getAllAreas: async (): Promise<GrowingArea[]> => {
    return apiClient.get<GrowingArea[]>('/growing-areas');
  },

  // Ziskej jednu oblast
  getArea: async (areaId: string): Promise<GrowingArea> => {
    return apiClient.get<GrowingArea>(`/growing-areas/${areaId}`);
  },

  // Ziskej sloty oblasti
  getSlots: async (areaId: string): Promise<AreaSlot[]> => {
    return apiClient.get<AreaSlot[]>(`/growing-areas/${areaId}/slots`);
  },

  // Ziskej obsazenost oblasti
  getOccupancy: async (areaId: string): Promise<OccupancyResult> => {
    return apiClient.get<OccupancyResult>(`/growing-areas/${areaId}/occupancy`);
  },

  // Ziskej doporuceni pro oblast
  getRecommendations: async (
    areaId: string,
    params?: RecommendationQueryParams,
  ): Promise<RecommendationResult> => {
    const queryParams = new URLSearchParams();
    if (params?.targetDate) queryParams.append('targetDate', params.targetDate);
    if (params?.categories) queryParams.append('categories', params.categories);
    if (params?.maxDifficulty) queryParams.append('maxDifficulty', String(params.maxDifficulty));
    if (params?.hydroponicOnly) queryParams.append('hydroponicOnly', 'true');
    if (params?.limit) queryParams.append('limit', String(params.limit));

    const query = queryParams.toString();
    const url = `/growing-areas/${areaId}/recommendations${query ? '?' + query : ''}`;
    return apiClient.get<RecommendationResult>(url);
  },

  // Vytvor novou oblast
  createArea: async (data: CreateGrowingAreaDTO): Promise<GrowingArea> => {
    return apiClient.post<GrowingArea>(
      '/growing-areas',
      data as unknown as Record<string, unknown>,
    );
  },

  // Aktualizuj oblast
  updateArea: async (areaId: string, data: UpdateGrowingAreaDTO): Promise<GrowingArea> => {
    return apiClient.patch<GrowingArea>(
      `/growing-areas/${areaId}`,
      data as Record<string, unknown>,
    );
  },

  // Smaz oblast
  deleteArea: async (areaId: string): Promise<void> => {
    return apiClient.delete(`/growing-areas/${areaId}`);
  },

  // Prirad rostlinu do slotu
  // Prirad rostlinu (nebo davku -> nova rostlina) do slotu
  assignPlantToSlot: async (
    slotId: string,
    plantId?: string,
    isAnchor?: boolean,
    batchId?: string,
    data?: any,
  ): Promise<PlantOccupancy> => {
    return apiClient.post(`/growing-areas/slots/${slotId}/assign`, {
      plantId,
      isAnchor,
      batchId,
      data,
    });
  },

  // Odstran rostlinu ze slotu
  removePlantFromSlot: async (slotId: string, plantId: string): Promise<void> => {
    return apiClient.delete(`/growing-areas/slots/${slotId}/plant/${plantId}`);
  },

  // Presun rostlinu mezi sloty (transplant)
  movePlant: async (
    plantId: string,
    fromSlotId: string,
    toSlotId: string,
  ): Promise<PlantOccupancy> => {
    return apiClient.post<PlantOccupancy>('/growing-areas/move-plant', {
      plantId,
      fromSlotId,
      toSlotId,
    });
  },

  // Ziskej historii udalosti slotu pro oblast
  getSlotEvents: async (
    areaId: string,
    params?: SlotEventQueryParams,
  ): Promise<SlotEventsResult> => {
    const queryParams = new URLSearchParams();
    if (params?.slot_id) queryParams.append('slot_id', params.slot_id);
    if (params?.event_type) queryParams.append('event_type', params.event_type);
    if (params?.plant_id) queryParams.append('plant_id', params.plant_id);
    if (params?.batch_id) queryParams.append('batch_id', params.batch_id);
    if (params?.from) queryParams.append('from', params.from);
    if (params?.to) queryParams.append('to', params.to);
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.offset) queryParams.append('offset', String(params.offset));

    const query = queryParams.toString();
    const url = `/growing-areas/${areaId}/slot-events${query ? '?' + query : ''}`;
    return apiClient.get<SlotEventsResult>(url);
  },

  // Vytvor manualni slot event (napr. cisteni)
  createSlotEvent: async (
    slotId: string,
    eventType: string,
    data?: { plant_id?: string; batch_id?: string; notes?: string; data?: Record<string, unknown> },
  ): Promise<SlotEvent> => {
    return apiClient.post(`/growing-areas/slots/${slotId}/events`, {
      event_type: eventType,
      ...data,
    });
  },
};
